/**
 * Pure rules of the ticket detail page (T-44, PRD §6.3, controls inventory
 * §4.3 / §4.4): the one state-driven primary, the guarded status select
 * (B.23), the `?tab=` param and how a failed load is shown. Unit-tested in
 * `src/utils/__tests__/ticketDetailModel.test.ts`.
 */
import { errorStatus } from '../ui/stateBlockModel';
import { TICKET_OPEN_STATUSES, isDoneStatus } from '../../utils/ticketStatus.shared';

export type TicketPrimary = 'uncancel' | 'reopen' | 'stop' | 'close' | 'start' | null;

/** The statuses the select may offer (closing goes through the primary / `useCloseTicket`, never the select). */
export const OPEN_STATUSES = ['planning', 'in_progress', 'in_review', 'testing'] as const;
/** D-146 — a service request's open statuses (חדשה / בטיפול / ממתינה ללקוח). */
export const OPEN_TICKET_SELECT_STATUSES: readonly string[] = TICKET_OPEN_STATUSES;

/** Statuses where the work is done and the ticket waits (for the customer, a review or a test): closing comes next. */
const WAITING = new Set(['in_review', 'testing', 'waiting']);

/**
 * The page's one primary, chosen by state (principle 4):
 * cancelled → "ביטול הביטול"; completed → "פתיחה מחדש"; my timer runs on it →
 * "עצירה ורישום"; waiting (in review / testing) → "סגירת הקריאה"; otherwise →
 * "התחלת עבודה". Customers get none.
 */
export function ticketPrimary(input: {
  isStaff: boolean;
  status: string;
  cancelled: boolean;
  timerRunningHere: boolean;
  /**
   * P-37 (D-194 Q-172) — on a SERVICE TICKET "אני על זה" (next to the composer)
   * replaces "התחלת עבודה": the header's primary is then "סגירת הקריאה".
   */
  taskType?: string | null;
  /** QA-P36 — false when the ticket is a discussion the caller may not close (opener / manager only). */
  mayClose?: boolean;
}): TicketPrimary {
  if (!input.isStaff) return null;
  if (input.cancelled) return 'uncancel';
  if (isDoneStatus(input.status)) return 'reopen';
  if (input.timerRunningHere) return 'stop';
  if (WAITING.has(input.status) && input.mayClose !== false) return 'close';
  return input.taskType === 'ticket' ? 'close' : 'start';
}

/**
 * Options of the side-panel status select. Never "הושלם" (B.23). A legacy
 * status still on the ticket (e.g. `open`) stays selectable as itself so the
 * select never shows a value the ticket does not have.
 */
export function statusSelectOptions(current: string, taskType?: string | null): string[] {
  // D-146 — a service request offers its own statuses, a task / discussion the task ones.
  // D-154 (QX-85) — a discussion is "פתוח" (in_progress) or "סגור" (the close action).
  if (taskType === 'discussion') return ['in_progress'];
  const base: string[] = taskType === 'ticket' ? [...OPEN_TICKET_SELECT_STATUSES] : [...OPEN_STATUSES];
  if (current && !isDoneStatus(current) && !base.includes(current)) base.unshift(current);
  return base;
}

/** The select is read-only while the ticket is completed (or cancelled): reopen / uncancel first. */
export function statusSelectLocked(status: string, cancelled: boolean): boolean {
  return isDoneStatus(status) || cancelled;
}

export type DetailTab = 'chat' | 'subtasks' | 'worklogs' | 'attachments';
const TABS: DetailTab[] = ['chat', 'subtasks', 'worklogs', 'attachments'];

/** `?tab=` → the tab on screen; unknown or unavailable values fall back to the conversation. */
export function parseDetailTab(param: string | null, available: readonly DetailTab[]): DetailTab {
  const tab = (TABS as string[]).includes(param ?? '') ? (param as DetailTab) : 'chat';
  return available.includes(tab) ? tab : 'chat';
}

/** 404 and 403 read "not found or no permission"; anything else is a real error with a retry. */
export function ticketLoadState(error: unknown): 'notFound' | 'error' {
  const s = errorStatus(error);
  return s === 404 || s === 403 ? 'notFound' : 'error';
}
